import { memo, type ReactNode, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { Command, useCommandState } from '@air/cmdk';

import { SearchCommandShortcutsFooter } from '~/components/SearchCommand/SearchCommandShortcutsFooter';

export type SearchCommandListFrameProps = {
  children: ReactNode;
};

export const SearchCommandListFrame = memo(({ children }: SearchCommandListFrameProps) => {
  const search = useCommandState((state) => state.search);
  const listRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const list = listRef.current;

    const scrollSuggestionsToTop = () => {
      list?.scrollTo({ top: 0 });
    };

    scrollSuggestionsToTop();
    list?.setAttribute('data-query', search);

    // cmdk's parent layout effect then scrollIntoView's the stale selected row.
    queueMicrotask(scrollSuggestionsToTop);
    const frame = requestAnimationFrame(scrollSuggestionsToTop);

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [search]);

  return (
    <div
      className="flex max-h-[min(480px,calc(100vh-8rem))] min-h-0 grow flex-col overflow-hidden ads:border-ads-gray-5 border-gray-5 border-t"
      data-testid="SEARCH_COMMAND_LIST_FRAME"
    >
      <Command.List
        // Prepending "Search for …" must not keep the previous viewport via overflow anchoring.
        className="flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto px-3 py-2 [overflow-anchor:none]"
        data-testid="SEARCH_COMMAND_LIST"
        ref={listRef}
      >
        {children}
      </Command.List>
      <SearchCommandShortcutsFooter />
    </div>
  );
});

SearchCommandListFrame.displayName = 'SearchCommandListFrame';
